import { IUserContactApiSchema } from "bee-library-api-client"

import { ENTITY_TYPE } from "common/constants/Entities"
import { COMMENT_SORTS } from "common/screens/modalComponents/comments/CommentsTypes"
import { IPeriodicalDeliveryAddress } from "common/services/periodicalApi/types/PeriodicalTypes"
import { RGPD_LABEL, RGPD_TITLE } from "common/services/rgpd/RgpdItems"

export interface ILibrary {
    id: string
    title: string
    url: string
}

export interface IAcknowledgement {
    date: Date
    feature: string
    version: number
    acknowledged: boolean
}

export interface IAcknowledgementsByLib {
    libId: string
    libName: string
    libAcknowledgements: IAcknowledgement[]
}

export type IAcknowledgementsSummary = IAcknowledgementsByLib[]

export interface IAcknowledgementsGroupItem {
    featureId: string
    acknowledged: boolean
}

export interface IAcknowledgementsGroup {
    label: RGPD_LABEL
    title?: RGPD_TITLE
    acknowledgements: {
        feature?: IAcknowledgementsGroupItem
        reporting?: IAcknowledgementsGroupItem
    }
}

export enum SUBSCRIPTION_FREQUENCIES {
    daily = "daily",
    weekly = "weekly",
    monthly = "monthly",
    never = "never",
}

export interface IContentSubscription {
    id: string
    type: ENTITY_TYPE.LIBRARY | ENTITY_TYPE.VIEW
    frequency: SUBSCRIPTION_FREQUENCIES
    viewId?: string
    viewPath?: string[]
}

export interface IContentSubscriptionsByLib {
    libId: string
    libName: string
    libSubscriptions: IContentSubscription[]
}

export interface IPeriodicalSubscription {
    id: string
    libraryId: string
    libraryName: string
    periodicalId: string
    periodicalName: string
    locale: string
    deliveryAddress: IPeriodicalDeliveryAddress
}

export enum EXTERNAL_CONTACT_ID {
    GMAIL = "gmail",
    MICROSOFT = "microsoft",
}

export const EXTERNAL_CONTACT_IDS_LIST = Object.values(EXTERNAL_CONTACT_ID)

export type IExternalIdentities = {
    [K in EXTERNAL_CONTACT_ID]: { enabled: boolean; email: string }
}

export interface IUserProfileRaw {
    userId: string
    email: string
    username: string
    firstName?: string
    lastName?: string
    address?: {
        country?: string
        address?: string
        zipCode?: string
        city?: string
        company?: string
        phoneNumber?: string
    }
    pictureUrl?: string
    picture?: string
    librariesWithAccessPermission?: ILibrary[]
    librariesWithAtLeastOneCollectionWithEditPermission?: ILibrary[]
    personalLibrary?: ILibrary
    acknowledgements?: IAcknowledgementsByLib[]
    subscriptions?: IContentSubscriptionsByLib[]
    periodical?: IPeriodicalSubscription[]
    userSettings?: { highContrastTheme?: boolean; preferredCommentSort?: COMMENT_SORTS }
    externalIdentity?: IExternalIdentities
}

export interface ISubscription {
    type: ENTITY_TYPE.LIBRARY | ENTITY_TYPE.VIEW | ENTITY_TYPE.PERIODICAL
    typeLabel: string
    label: string
    viewPath?: string[]
    frequency?: SUBSCRIPTION_FREQUENCIES
    viewId?: string
    id?: string
    periodicalId?: string
}

export interface ISubscriptionsByLib {
    libId: string
    libName: string
    libSubscriptions: ISubscription[]
}

interface IUserSettingsByLibrary {
    layout: string
}

export interface IUserSettings {
    highContrastTheme?: boolean
    animatedThumb?: boolean
    preferredCommentSort?: COMMENT_SORTS
    byLibrary?: { [libId: string]: IUserSettingsByLibrary }
}

export type ISubscriptionsSummary = {
    libId: string
    libName: string
    subscriptionsCount: number
}[]

export interface IUserProfileCooked {
    userId: string
    email: string
    username: string
    firstName: string
    lastName: string
    fullName: string
    displayName: string
    address: {
        country: string
        address: string
        zipCode: string
        city: string
        company: string
        phoneNumber: string
    }
    pictureUrl: string
    librariesWithAccessPermission: ILibrary[]
    librariesWithAtLeastOneCollectionWithEditPermission: ILibrary[]
    personalLibrary?: ILibrary
    acknowledgements: IAcknowledgementsByLib[]
    subscriptions: IContentSubscriptionsByLib[]
    periodical: IPeriodicalSubscription[]
    userSettings: IUserSettings
    externalIdentity?: IExternalIdentities
}

export type IContact = IUserContactApiSchema
